import React, { FC, useMemo } from "react";
import classNames from "classnames/dedupe";
import format from "date-fns/format";
import { LocationPinIcon, PasswordRevealIcon } from "../../../../icons";
import { SvgIcon } from "../../../Icons";
import { Typography } from "../../../Fundamentals/Typography";
import {
  deliveryTypeText,
  TOrder,
  OrderStatus,
  orderStatusIcon,
  orderStatusText,
  Product,
} from "./Order.interface";
import { Column, Row } from "../../../Fundamentals/Grid";
import { DEFAULT_DATE_FORMAT } from "../../../../helpers";
import { Image } from "../../../Utility/MediaBuilder/Image";
import { LinkButton } from "../../../ActionsSelectors/Button";
import Highlight, { IHighlightProps } from "../Highlight/Highlight";
import { searchInProduct } from "./searchInProduct";
import { Link } from "../../../ActionsSelectors/Link";

export interface IOrderInformationProps
  extends Partial<Pick<IHighlightProps, "highlight">> {
  /**
   * Property documentation
   */
  id?: string;
  classes?: {
    root?: string;
  };
  order: TOrder;
  currency?: string;
  highlightedProductIds?: Array<Product["id"]>;
  customMedia?: JSX.Element;
  showTitle?: boolean;
}

const MAX_VISIBLE_IMAGES = 4;

const OrderInformation: FC<IOrderInformationProps> = ({
  classes,
  order,
  currency = "€",
  highlight,
  highlightedProductIds,
  customMedia,
  showTitle = true,
}) => {
  const highlightedProducts = useMemo(() => {
    if (highlightedProductIds) return highlightedProductIds;
    if (!highlight) return [];
    return order.products
      .filter(searchInProduct(highlight))
      .map((product) => product.id);
  }, [highlightedProductIds, highlight, order.products]);

  return (
    <div
      data-testid="OrderInformation"
      className={`p-7 bg-color-grey-200 text-color-blue ${classes?.root || ""}`}
    >
      {showTitle && (
        <div className="mb-7" data-testid="order-title">
          <div
            className={classNames("d-flex align-items-center", {
              "text-color-error": order.status === OrderStatus.CANCELLED,
              "text-color-blue": order.status !== OrderStatus.CANCELLED,
            })}
          >
            {orderStatusIcon[order.status] ? (
              <SvgIcon
                type="svg-icon"
                icon={orderStatusIcon[order.status]}
                classes={{ root: "icon-container--small me-4" }}
              />
            ) : null}
            <Typography
              variant="text-menu"
              classes={{
                root: classNames("fontfix"),
              }}
            >
              <Highlight
                text={orderStatusText[order.status] ?? "Unknown"}
                highlight={highlight}
              />
            </Typography>
          </div>
        </div>
      )}
      <div>
        <Row>
          <Column xs={12} lg={6} classes={{ root: "print-w-100" }}>
            <Row>
              <Column
                xs={6}
                lg={4}
                classes={{ root: "mb-6 order-lg-1" }}
                data-testid="order-number"
              >
                <Typography
                  variant="text-desc"
                  as="div"
                  classes={{ root: "mb-3 text-color-grey-700" }}
                >
                  Order #:
                </Typography>
                <Typography
                  variant="p-medium"
                  as="div"
                  classes={{ root: "text-color-blue" }}
                >
                  <Highlight text={order.id} highlight={highlight} />
                </Typography>
              </Column>
              {order.status !== undefined && (
                <Column
                  xs={6}
                  lg={4}
                  classes={{ root: "mb-6 order-lg-0" }}
                  data-testid="order-status"
                >
                  <Typography
                    variant="text-desc"
                    as="div"
                    classes={{ root: "mb-3 text-color-grey-700" }}
                  >
                    Status:
                  </Typography>
                  <Typography
                    variant="p-medium"
                    as="div"
                    classes={{ root: "text-color-blue" }}
                  >
                    <Highlight
                      text={orderStatusText[order.status] ?? "Unknown"}
                      highlight={highlight}
                    />
                  </Typography>
                </Column>
              )}
              <Column
                xs={6}
                lg={4}
                classes={{ root: "mb-6 order-lg-3" }}
                data-testid="order-date"
              >
                <Typography
                  variant="text-desc"
                  as="div"
                  classes={{ root: "mb-3 text-color-grey-700" }}
                >
                  Date:
                </Typography>
                <Typography
                  variant="p-medium"
                  as="div"
                  classes={{ root: "text-color-blue" }}
                >
                  <Highlight
                    text={format(
                      new Date(order.createdAt),
                      DEFAULT_DATE_FORMAT,
                    )}
                    highlight={highlight}
                  />
                </Typography>
              </Column>
              {order.totalProducts > 0 && (
                <Column
                  xs={6}
                  lg={4}
                  classes={{ root: "mb-6 order-lg-5" }}
                  data-testid="order-total-products"
                >
                  <Typography
                    variant="text-desc"
                    as="div"
                    classes={{ root: "mb-3 text-color-grey-700" }}
                  >
                    Items:
                  </Typography>
                  <Typography
                    variant="p-medium"
                    as="div"
                    classes={{ root: "text-color-blue" }}
                  >
                    {order.totalProducts}
                  </Typography>
                </Column>
              )}
              {order.delivery && (
                <Column
                  xs={6}
                  lg={4}
                  classes={{ root: "mb-6 order-lg-4" }}
                  data-testid="order-delivery"
                >
                  <Typography
                    variant="text-desc"
                    as="div"
                    classes={{ root: "mb-3 text-color-grey-700" }}
                  >
                    Delivery:
                  </Typography>
                  <Typography
                    variant="p-medium"
                    as="div"
                    classes={{ root: "text-color-blue" }}
                  >
                    <Highlight
                      text={
                        order?.delivery?.type !== undefined
                          ? deliveryTypeText[order.delivery.type]
                          : "unknown"
                      }
                      highlight={highlight}
                    />
                  </Typography>
                </Column>
              )}
              <Column
                xs={6}
                lg={4}
                classes={{ root: "mb-6 order-lg-2" }}
                data-testid="order-amount"
              >
                <Typography
                  variant="text-desc"
                  as="div"
                  classes={{ root: "mb-3 text-color-grey-700" }}
                >
                  Amount:
                </Typography>
                <Typography
                  variant="p-medium"
                  as="div"
                  classes={{ root: "text-color-blue" }}
                >
                  <span>{currency}</span>
                  <Highlight text={String(order.price)} highlight={highlight} />
                </Typography>
              </Column>
            </Row>
            <Row classes={{ root: "d-none d-lg-flex" }}>
              <Column xs={6} lg={4} classes={{ root: "no-print" }}>
                <LinkButton
                  classes={{
                    root: "text-decoration-none white-space-nowrap",
                    content: "button-link__text",
                  }}
                  variant="icon"
                  startIcon={PasswordRevealIcon}
                >
                  View Details
                </LinkButton>
              </Column>
              {order.trackOrderLink ? (
                <>
                  <Column xs={12} lg={4}>
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      classes={{
                        root: classNames(
                          "text-color-blue text-decoration-none",
                          order?.trackOrderLink?.classes?.root,
                        ),
                      }}
                      {...(order?.trackOrderLink || {})}
                    >
                      <LinkButton
                        classes={{
                          root: "text-decoration-none white-space-nowrap",
                          content: "button-link__text",
                        }}
                        variant="icon"
                        startIcon={LocationPinIcon}
                      >
                        Track Order
                      </LinkButton>
                    </Link>
                  </Column>
                  <Column xs={12} classes={{ root: "print-only" }}>
                    <Typography>
                      {order.trackOrderLink.text || order.trackOrderLink.href}
                    </Typography>
                  </Column>
                </>
              ) : null}
            </Row>
          </Column>
          {order.products && (
            <Column
              xs={12}
              lg={6}
              classes={{ root: "no-print  mb-6" }}
              data-testid="order-products"
            >
              <div className="d-flex justify-content-lg-end">
                {order.products.map((product, index) => {
                  if (
                    order.products.length > MAX_VISIBLE_IMAGES &&
                    index === MAX_VISIBLE_IMAGES - 1
                  ) {
                    return (
                      <div
                        className="w-56px w-lg-80px h-56px h-lg-80px d-flex justify-content-center align-items-center me-4  border rounded-1 bg-color-white"
                        key={product.id}
                      >
                        <span className="text-menu">
                          {order.products.length - MAX_VISIBLE_IMAGES}+
                        </span>
                      </div>
                    );
                  }
                  if (index <= MAX_VISIBLE_IMAGES - 1) {
                    return (
                      <div
                        key={product.id}
                        data-testid="OrderInformation__image"
                        className={classNames(
                          "w-56px w-lg-80px h-56px h-lg-80px d-flex justify-content-center align-items-center me-4 rounded-1 border",
                          {
                            "border-color-blue": highlightedProducts.includes(
                              product.id,
                            ),
                          },
                        )}
                        title={`${product.brand || ""} ${product.itemName}`}
                      >
                        {customMedia ?? (
                          <Image
                            classes={{ root: "w-100 h-100" }}
                            src={product.image}
                            alt={product.imageAlt}
                          />
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </Column>
          )}
          <Column xs={12} classes={{ root: "d-flex d-lg-none no-print" }}>
            <LinkButton
              classes={{
                iconRoot: "me-6",
                root: "text-decoration-none white-space-nowrap",
                content: "button-link__text",
              }}
              variant="icon"
              startIcon={PasswordRevealIcon}
            >
              View Details
            </LinkButton>
            {order.trackOrderLink ? (
              <Link
                target="_blank"
                rel="noreferrer"
                classes={{
                  root: classNames(
                    "text-decoration-none text-color-blue d-flex",
                    order.trackOrderLink?.classes?.root,
                  ),
                }}
                {...(order.trackOrderLink || {})}
              >
                <LinkButton
                  classes={{
                    root: "text-decoration-none white-space-nowrap",
                    content: "button-link__text",
                  }}
                  variant="icon"
                  startIcon={LocationPinIcon}
                >
                  Track Order
                </LinkButton>
              </Link>
            ) : null}
          </Column>
        </Row>
      </div>
    </div>
  );
};

export default OrderInformation;
